// TrésoPilot — Parrainage (Referral)
//
// Flux:
// 1) L’utilisateur arrive via un lien du type /register?ref=XXXX
// 2) On stocke le code en localStorage (pour survivre aux navigations / OAuth redirects).
// 3) Une fois connecté (session Supabase), on appelle la RPC `apply_referral(ref_code)`.

const STORAGE_KEY = 'tresopilot_referral';
const APPLIED_PREFIX = 'tresopilot_referral_applied:';
const APPLIED_PAYLOAD_PREFIX = 'tresopilot_referral_applied_payload:';

// Cookie fallback (useful when users navigate between subdomains, or when
// localStorage is temporarily unavailable in some webviews).
const COOKIE_KEY = 'tresopilot_referral';

// Durée max de conservation du code (côté navigateur)
const MAX_AGE_DAYS = 30;

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function lsGet(key) {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key, value) {
  try {
    if (typeof localStorage === 'undefined') return false;
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function lsRemove(key) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function getRootCookieDomain() {
  // Share referral across subdomains when possible (e.g. app.tresopilot.fr <-> www.tresopilot.fr).
  // Heuristic: keep the last 2 hostname labels (works for .fr).
  // For localhost / IPs, return null so we don't set Domain=.
  try {
    if (!isBrowser()) return null;
    const host = String(window.location.hostname || '').trim();
    if (!host) return null;
    if (host === 'localhost' || host.endsWith('.localhost')) return null;
    // IPv4 / IPv6
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;
    if (host.includes(':')) return null;

    const parts = host.split('.').filter(Boolean);
    if (parts.length < 2) return null;
    const root = parts.slice(-2).join('.');
    return `.${root}`;
  } catch {
    return null;
  }
}

function setCookie(name, value, maxAgeDays) {
  if (!isBrowser()) return;
  try {
    const maxAge = Math.max(1, Number(maxAgeDays || MAX_AGE_DAYS)) * 24 * 60 * 60;
    const domain = getRootCookieDomain();
    const secure = window.location?.protocol === 'https:' ? '; Secure' : '';
    const domainPart = domain ? `; Domain=${domain}` : '';
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${domainPart}${secure}`;
  } catch {
    // ignore
  }
}

function getCookie(name) {
  if (!isBrowser()) return null;
  try {
    const n = encodeURIComponent(name) + '=';
    const parts = String(document.cookie || '').split(';');
    for (const p of parts) {
      const s = p.trim();
      if (s.startsWith(n)) return decodeURIComponent(s.slice(n.length));
    }
    return null;
  } catch {
    return null;
  }
}

function deleteCookie(name) {
  if (!isBrowser()) return;
  try {
    const domain = getRootCookieDomain();
    const secure = window.location?.protocol === 'https:' ? '; Secure' : '';
    const domainPart = domain ? `; Domain=${domain}` : '';
    document.cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax${domainPart}${secure}`;
  } catch {
    // ignore
  }
}

function nowMs() {
  return Date.now();
}

function storeReferral({ code, source } = {}) {
  if (!isBrowser()) return;
  const c = String(code || '').trim();
  if (!c) return;
  const s = String(source || 'referral').trim() || 'referral';
  const payload = JSON.stringify({ code: c, source: s, ts: nowMs() });
  // Prefer localStorage when available, but always write a cookie fallback.
  lsSet(STORAGE_KEY, payload);
  setCookie(COOKIE_KEY, payload, MAX_AGE_DAYS);
}

export function captureReferralFromSearch(search) {
  if (!isBrowser()) return;
  try {
    const sp = new URLSearchParams(search || '');
    const amb = sp.get('amb') || sp.get('a') || sp.get('ambassador');
    const raw = amb || sp.get('ref') || sp.get('r') || sp.get('referral');
    if (!raw) return;
    const code = String(raw).trim();
    if (!code) return;
    const src = String(sp.get('src') || '').toLowerCase();
    const source = amb ? 'ambassador' : (src.includes('amb') ? 'ambassador' : 'referral');
    storeReferral({ code, source });
  } catch {
    // ignore
  }
}

export function getStoredReferralCode() {
  if (!isBrowser()) return null;
  const raw = lsGet(STORAGE_KEY) || getCookie(COOKIE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const code = String(parsed?.code || '').trim();
    const ts = Number(parsed?.ts || 0);
    if (!code || !Number.isFinite(ts) || ts <= 0) return null;
    const ageDays = (nowMs() - ts) / (1000 * 60 * 60 * 24);
    if (ageDays > MAX_AGE_DAYS) return null;
    return code;
  } catch {
    return null;
  }
}

export function getStoredReferralSource() {
  if (!isBrowser()) return null;
  const raw = lsGet(STORAGE_KEY) || getCookie(COOKIE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const src = String(parsed?.source || '').trim();
    return src || null;
  } catch {
    return null;
  }
}

export function storeAmbassadorCode(code) {
  storeReferral({ code, source: 'ambassador' });
}

export function clearStoredReferral() {
  if (!isBrowser()) return;
  lsRemove(STORAGE_KEY);
  deleteCookie(COOKIE_KEY);
}

// Store a lightweight marker that a referral/ambassador link was applied for this user.
// This survives the clearStoredReferral() so the UI can show a one-time message on first dashboard visit.
function setReferralAppliedPayload(userId, { code, source } = {}) {
  if (!isBrowser() || !userId) return;
  try {
    const payload = JSON.stringify({
      code: String(code || '').trim(),
      source: String(source || 'referral').trim() || 'referral',
      ts: nowMs(),
    });
    lsSet(`${APPLIED_PAYLOAD_PREFIX}${userId}`, payload);
  } catch {
    // ignore
  }
}

export function getReferralAppliedPayload(userId) {
  if (!isBrowser() || !userId) return null;
  const raw = lsGet(`${APPLIED_PAYLOAD_PREFIX}${userId}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const code = String(parsed?.code || '').trim();
    const source = String(parsed?.source || '').trim() || 'referral';
    const ts = Number(parsed?.ts || 0);
    if (!code || !Number.isFinite(ts) || ts <= 0) return null;
    const ageDays = (nowMs() - ts) / (1000 * 60 * 60 * 24);
    if (ageDays > MAX_AGE_DAYS) return null;
    return { code, source, ts };
  } catch {
    return null;
  }
}

export async function applyStoredReferral({ supabase, userId }) {
  if (!isBrowser()) return { ok: false, skipped: true };
  if (!supabase || !userId) return { ok: false, skipped: true };

  const appliedKey = `${APPLIED_PREFIX}${userId}`;
  if (lsGet(appliedKey)) return { ok: true, already: true };

  const code = getStoredReferralCode();
  const source = getStoredReferralSource() || 'referral';
  if (!code) return { ok: false, skipped: true };

  try {
    const { data, error } = await supabase.rpc('apply_referral', { ref_code: code, source });
    // Même si la RPC retourne ok=false (code invalide / expiré), on évite la boucle infinie.
    if (error) {
      // In case table/function not deployed yet, don't crash auth.
      console.warn('[referral] apply_referral failed:', error);
      clearStoredReferral();
      lsSet(appliedKey, '1');
      return { ok: false, error };
    }

    // Keep a tiny marker so we can show a one-time UI message on first dashboard visit.
    // Only store it when the RPC doesn't error (even if it returns ok=false).
    setReferralAppliedPayload(userId, { code, source: data?.source || source });

    clearStoredReferral();
    lsSet(appliedKey, '1');
    return data || { ok: true };
  } catch (e) {
    console.warn('[referral] apply_referral exception:', e);
    clearStoredReferral();
    lsSet(appliedKey, '1');
    return { ok: false, error: e };
  }
}
